import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import Stack from "@mui/material/Stack";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiCopy, FiEdit, FiPlusCircle, FiXCircle } from "react-icons/fi";
import * as yup from "yup";

import { useAppState } from "../../../context/StateContext";
import useYupValidation from "../../../hooks/useYupValidation";
import { Contact } from "../../../models/contact";
import { Customer } from "../../../models/customer";
import { Languages } from "../../../models/languages";
import { CustomerLocations } from "../../../models/sales";
import { Timezone } from "../../../models/timezone";
import CustomerMaintenance from "../../../pages/private/Shared/Network/Customer/Maintenance";
import ContactService from "../../../services/contact.service";
import CustomerService from "../../../services/customer.service";
import { Status } from "../../../util/enums";
import { formatGMT } from "../../../util/Format";
import {
  formatPhoneNumber,
  phoneInputSettings,
  phoneNumberIsValid,
} from "../../../util/formatPhoneNumber";
import getOptionLabelCustomer from "../../../util/getOptionLabelCustomer";
import AutoSuggest from "../../Form/AutoSuggest";
import Button, { Color } from "../../Form/Button";
import Input from "../../Form/Input";
import InputPhone from "../../Form/PhoneInput";
import useStyles from "./styles";
import { resolveDestinationMobile } from "./destinationMobile";

type CustomerInfoType = {
  id?: number;
  fullName?: string;
  lastName?: string;
  name: string;
  phone: string;
  mobile: string;
  email: string;
  contacts?: Contact[];
  timezoneId?: number;
  timezone?: Timezone;
  languageId?: number | null;
  abbreviation?: string;
  destinationMobile: string;
  companyId?: number;
};

type LanguageId = Pick<Languages, "id">;
interface CustomerInfoProps {
  customer?: Customer;
  contacts?: Contact[];
  typeAddress: "pickup" | "delivery" | string;
  update: (value: CustomerLocations) => void;
  onCopyInfo?: (from: "pickup" | "delivery" | string) => void;
  timezones?: Timezone[];
  languageOptions?: Languages[];
  customerLanguage?: Languages;
  isDelivery?: boolean;
  externalErrors?: yup.ValidationError[];
  contactsErrors?: yup.ValidationError[][];
  userLanguageId?: number;
}

type CustomerAttributes = {
  name: string;
  email: string;
  phone: String;
  mobile: string;
  fullName: string;
  lastName: string;
  languageId: LanguageId | null;
  abbreviation: string;
  timezoneId: string;
  destinationMobile: string;
};

type ContactAttributes = {
  firstName: string;
  mobilePhone: string;
  email: String;
};

type Attributes = keyof CustomerAttributes | keyof ContactAttributes;

const CustomerInfoComponent = ({
  customer,
  contacts,
  typeAddress,
  onCopyInfo,
  update,
  timezones = [],
  languageOptions,
  customerLanguage,
  isDelivery,
  externalErrors = [],
}: CustomerInfoProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser, isFMorSPVersion } = useAppState();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { getErrorByField, fieldHasError } = useYupValidation([...errors, ...externalErrors]);

  const [data, setData] = useState<CustomerInfoType>();
  const [dataContacts, setDataContacts] = useState<Contact[]>();
  const [timezoneValue, setTimezoneValue] = useState<string>("");

  const [toggleEditCustomerModal, setToggleEditCustomerModal] = useState<boolean>(false);

  const [isDeliveryInputOnFocus, setIsDeliveryInputOnFocus] = useState<boolean>(false);
  const [removeDefaultCountry, setRemoveDefaultCountry] = useState<boolean>(false);
  const [englishId, setEnglishId] = useState<number | undefined>();
  const [englishLanguage, setEnglishLanguage] = useState<Languages | undefined>();

  const getEmptyContact = () => {
    return {
      firstName: "",
      lastName: "",
      email: "",
      mobilePhone: "",
      valid: true,
      companyId: currentUser?.companyId,
    } as Contact;
  };

  const isCustomerSelected = () => customer && customer.id;

  const validateForm = () => {
    try {
      const schema = yup.object().shape({
        emailCustomer: yup.string().email(translate("thisEmailIsInvalid")),
        // timezoneId: yup
        //   .number()
        //   .required(translate("thisFieldIsRequired"))
        //   .typeError(translate("thisFieldIsRequired")),
      });

      schema.validateSync(
        {
          emailCustomer: getCustomerValue("email"),
          timezoneId: getCustomerValue("timezoneId"),
        },
        { abortEarly: false }
      );

      setErrors([]);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
      }
    }
  };

  const typeAddressInverted = () => {
    if (typeAddress === "pickup") return "Delivery";

    return "Pickup";
  };

  const defaultLanguageId = useCallback(() => {
    if (languageOptions) {
      const english: Languages | undefined = languageOptions.find(
        (language) => language.name === "English"
      );

      setEnglishId(english?.id);
      setEnglishLanguage(english);
    }
  }, [languageOptions]);

  useEffect(() => defaultLanguageId(), [languageOptions]);

  const getCustomer = useCallback(
    (customer?: Customer) => {
      let customerObject = {} as CustomerInfoType;

      const currentTimezone: string =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Sydney";

      if (customer && customer.timezoneId) {
        let timezoneFound: Timezone | undefined;

        if (timezones && timezones?.length > 0) {
          timezoneFound = timezones.find((timezone) => timezone.id === customer?.timezoneId);

          if (timezoneFound) {
            setTimezoneValue(
              `GMT (${formatGMT(timezoneFound?.gmtOffset)}) ${timezoneFound?.nameFormatted
                ?.replace("/", " / ")
                .replace("_", "")}`
            );
          }
        }

        if (customer?.id) {
          customerObject = {
            ...customer,
            companyId: currentUser?.companyId,
            name: String(customer?.name),
            phone: formatPhoneNumber(String(customer?.phone ?? "")),
            mobile: formatPhoneNumber(String(customer?.mobile ?? "")),
            email: String(customer?.email),
            fullName: `${customer?.name ?? ""} ${customer?.lastName ?? ""}`,
            lastName: customer?.lastName ?? "",
            timezone: timezoneFound,
            timezoneId: customer?.timezoneId,
            destinationMobile: resolveDestinationMobile(
              customer?.destinationMobile,
              customer?.mobile,
              customer?.phone
            ),
            languageId:
              customer.languageId === null || undefined ? englishId : Number(customer?.languageId),
            abbreviation: String(customer?.abbreviation || ""),
          };
        } else {
          customerObject = {
            companyId: currentUser?.companyId,
            name: String(customer?.name ?? ""),
            phone: formatPhoneNumber(String(customer?.phone ?? "")),
            mobile: formatPhoneNumber(String(customer?.mobile ?? "")),
            email: String(customer?.email ?? ""),
            fullName: `${customer?.name ?? ""} ${customer?.lastName ?? ""}`,
            lastName: customer?.lastName ?? "",
            timezone: timezoneFound,
            timezoneId: customer?.timezoneId,
            destinationMobile: formatPhoneNumber(String(customer.destinationMobile || "")),
            languageId:
              customer.languageId === null || undefined ? englishId : Number(customer?.languageId),
            abbreviation: String(customer?.abbreviation || ""),
          };
        }
      } else if (timezones && timezones.length > 0) {
        const timezoneFound = timezones.find((timezone) => timezone.name === currentTimezone);

        if (timezoneFound) {
          if (!customer) {
            setTimezoneValue(
              `GMT (${formatGMT(timezoneFound?.gmtOffset)}) ${timezoneFound?.nameFormatted
                ?.replace("/", " / ")
                .replace("_", "")}`
            );
            customerObject = {
              companyId: currentUser?.companyId,
              name: "",
              phone: "",
              mobile: "",
              destinationMobile: "",
              email: "",
              fullName: "",
              lastName: "",
              languageId: englishId,
              abbreviation: "",
              timezoneId: timezoneFound ? timezoneFound.id : undefined,
              timezone: timezoneFound,
            };
          } else {
            customerObject = {
              ...(customer || {}),
              companyId: currentUser?.companyId,
              name: customer.name ?? data?.name ?? "",
              phone: formatPhoneNumber(customer.phone ?? data?.phone ?? ""),
              mobile: formatPhoneNumber(customer.mobile ?? data?.mobile ?? ""),
              destinationMobile: resolveDestinationMobile(
                customer.destinationMobile,
                customer.mobile,
                customer.phone
              ),
              abbreviation: String(customer?.abbreviation ?? data?.abbreviation ?? ""),
              languageId: Number(customer?.languageId ?? data?.languageId ?? englishId),
              email: customer?.email ?? data?.email ?? "",
              lastName: customer.lastName ?? data?.lastName ?? "",
              fullName: customer.fullName ?? data?.fullName ?? "",
              timezone: timezoneFound,
              timezoneId: timezoneFound.id,
            };
          }
        }
      } else {
        let timezoneFound: Timezone | undefined;

        if (!customer?.timezoneId && !customer?.id) {
          const timezoneFound = timezones?.find((timezone) => timezone.name === currentTimezone);

          if (timezoneFound)
            setTimezoneValue(
              `GMT (${formatGMT(timezoneFound?.gmtOffset)}) ${timezoneFound?.nameFormatted
                ?.replace("/", " / ")
                .replace("_", "")}`
            );
        }

        if (customer?.id) {
          customerObject = {
            ...customer,
            companyId: currentUser?.companyId,
            name: String(customer?.name),
            lastName: String(customer?.lastName),
            phone: formatPhoneNumber(String(customer?.phone ?? "")),
            mobile: formatPhoneNumber(String(customer?.mobile ?? "")),
            email: String(customer?.email),
            timezone: timezoneFound,
            timezoneId: customer?.timezoneId,
            destinationMobile: resolveDestinationMobile(
              customer.destinationMobile,
              customer?.mobile,
              customer?.phone
            ),
            abbreviation: String(customer?.abbreviation || ""),
            languageId:
              customer.languageId === null || undefined ? englishId : Number(customer?.languageId),
          };
        } else {
          customerObject = {
            companyId: currentUser?.companyId,
            lastName: String(customer?.lastName),
            name: String(customer?.name),
            phone: formatPhoneNumber(String(customer?.phone ?? "")),
            mobile: formatPhoneNumber(String(customer?.mobile ?? "")),
            email: String(customer?.email),
            fullName: `${customer?.name ?? ""} ${customer?.lastName ?? ""}`,
            timezone: timezoneFound,
            timezoneId: customer?.timezoneId ? customer?.timezoneId : timezoneFound?.id,
            destinationMobile: resolveDestinationMobile(
              customer?.destinationMobile,
              customer?.mobile
            ),
            abbreviation: String(customer?.abbreviation || ""),
            languageId:
              customer?.languageId === null || undefined ? englishId : Number(customer?.languageId),
          };
        }
      }

      if (customer && !customer.name?.trim().length) {
        setData({
          ...customer,
          phone: formatPhoneNumber(customer?.phone ?? ""),
          mobile: formatPhoneNumber(customer?.mobile ?? ""),
          destinationMobile: formatPhoneNumber(customer.destinationMobile),
        } as CustomerInfoType);
      } else {
        setData(() => ({
          ...customer,
          companyId: currentUser?.companyId,
          id: customerObject?.id,
          lastName: customerObject.lastName || "",
          name: customerObject.name || "",
          phone: formatPhoneNumber(customerObject.phone || ""),
          mobile: formatPhoneNumber(customerObject.mobile || ""),
          destinationMobile: formatPhoneNumber(customerObject.destinationMobile),
          email: customerObject?.email || "",
          fullName: customerObject.fullName || "",
          timezone: customerObject.timezone,
          timezoneId: customerObject.timezoneId,
          languageId: customerObject.languageId || englishId,
          abbreviation: customerObject.abbreviation || "",
        }));
      }
    },
    [customer, timezones, data]
  );

  useEffect(() => {
    setDataContacts(contacts);
  }, [contacts]);

  useEffect(() => {
    validateForm();
  }, [data]);

  useEffect(() => {
    getCustomer(customer);
  }, [customer, timezones]);

  const getCustomerValue = (attribute: Attributes): string => {
    if (data) {
      if (data[attribute as keyof CustomerAttributes] !== "undefined") {
        const value = data[attribute as keyof CustomerAttributes];

        const valueInAttribute = String(value !== undefined && value !== null ? value : "");

        if (["fullName", "name", "lastName"].includes(attribute)) {
          if (valueInAttribute?.length <= 1) return valueInAttribute?.trim();
        }

        if (["phone", "destinationMobile", "mobile"].includes(attribute)) {
          return valueInAttribute;
        }

        if (valueInAttribute?.length) return valueInAttribute;

        return "";
      }
    }

    return "";
  };

  const setCustomerInputValue = (attribute: Attributes, value: any) => {
    const copy = { ...data } as any;

    copy[attribute as keyof CustomerAttributes] = value;

    switch (attribute) {
      case "fullName":
        copy.name = value;
        break;

      case "name":
        copy.name = value;
        break;

      case "lastName":
        copy.lastName = value;
        break;

      case "abbreviation":
        copy.abbreviation = value;
        break;

      case "languageId":
        copy.language = value;
        break;

      // ADD ATTRIBUTE CASES HERE
      default:
        return copy;
    }
    setData(copy);
    _.debounce(() => update({ contacts, customer: { ...copy } }), 500);
  };

  const setContactValue = (attribute: keyof ContactAttributes, value: any, index: number) => {
    const copyContacts = [...(dataContacts || [])] as Contact[];

    if (copyContacts[index]) {
      const contact: Contact = {
        ...copyContacts[index],
        firstName: copyContacts[index].firstName,
        lastName: copyContacts[index].lastName,
        email: copyContacts[index]?.email,
        mobilePhone: copyContacts[index].mobilePhone,
      } as Contact;

      contact[attribute as keyof ContactAttributes] = value;
      copyContacts[index] = contact;
    }

    setDataContacts(copyContacts);
    update({
      contacts: copyContacts,
      customer: data as any,
    });
  };

  const setCustomerValue = (attribute: Attributes, value: any) => {
    const copy = { ...data } as any;

    copy[attribute as keyof CustomerAttributes] = value;
    update({ customer: { ...copy, companyId: currentUser?.companyId }, contacts: dataContacts });
  };

  const setContactOnSearch = (contact: Contact, index: number) => {
    const copyContacts = [...(dataContacts || [])] as Contact[];
    const newContact = contact?.id ? contact : getEmptyContact();

    if (copyContacts[index]) {
      copyContacts[index] = newContact;
    }

    setDataContacts(copyContacts);
    update({
      contacts: copyContacts,
      customer: data as any,
    });
  };

  const onAddSecondContact = () => {
    const newContacts = [...(dataContacts || [])] as Contact[];

    if (newContacts) {
      newContacts.push(getEmptyContact());

      setDataContacts(newContacts);
      update({ contacts: newContacts, customer: data as any });
    }
  };

  const onRemoveSecondContact = (index: number) => {
    const newContacts = [...(dataContacts || [])] as Contact[];

    if (newContacts[index]) newContacts[index].valid = false;

    setDataContacts(newContacts);
    update({
      contacts: newContacts,
      customer: data as any,
    });
  };

  const updateWithFirstContact = (customer: Customer) => {
    const newContacts: Contact[] = [];

    if (customer?.contacts && !!customer.contacts.length) {
      newContacts.push(customer.contacts[0]);
    }

    setDataContacts(newContacts);
    update({ contacts: newContacts, customer });
  };

  const handleSetCustomerFields = (customer?: Customer) => {
    if (!customer) {
      const emptyCustomerInfo: CustomerInfoType = {
        name: "",
        phone: "",
        mobile: "",
        email: "",
        fullName: "",
        lastName: "",
        abbreviation: "",
        id: undefined,
        timezoneId: 63,
        contacts: [],
        destinationMobile: "",
      };

      setData(emptyCustomerInfo);
      setTimeout(() => update({ customer: emptyCustomerInfo, contacts: dataContacts }), 2000);
    } else {
      let mobilePhone =
        isDelivery && customer?.destinationMobile ? customer?.destinationMobile : customer?.mobile;

      const propertyName = isDelivery ? "destinationMobile" : "mobile";

      if (mobilePhone) {
        if (!mobilePhone.includes("+") && isFMorSPVersion) {
          mobilePhone = `+${mobilePhone}`;
        }
      }

      updateWithFirstContact({ ...customer, [propertyName]: mobilePhone });
    }
  };

  const handleFilterContactOptions = (contactsSearched: Contact[]) => {
    return contactsSearched?.filter(
      (contact) =>
        !contacts?.find(
          (contactAdded) => contactAdded.id === contact.id && contactAdded.valid !== false
        )
    );
  };

  const renderEditCustomerButton = () => {
    if (isCustomerSelected()) {
      return (
        <FiEdit
          color="#475467"
          className={classes.iconAddButton}
          onClick={() => setToggleEditCustomerModal(true)}
        />
      );
    }
  };

  const getContactValue = (contact: Contact, attribute: string) => {
    if (contact) {
      if (contact[attribute as keyof ContactAttributes] !== "undefined") {
        const valueInAttribute = String(contact[attribute as keyof ContactAttributes]);

        if (["firstName", "lastName"].includes(attribute)) {
          if (valueInAttribute?.length <= 1) return valueInAttribute?.trim();
        }

        if (["phone", "mobilePhone"].includes(attribute)) {
          return valueInAttribute;
        }
        if (valueInAttribute?.length) return valueInAttribute;

        return "";
      }
    }

    return "";
  };
  const mobilePhoneInputName = useMemo(
    () => (isDelivery ? "destinationMobile" : "mobile"),
    [isDelivery]
  );
  const renderCustomerFields = () => {
    return (
      <>
        <Stack spacing={1} direction="row" alignItems="center">
          <Grid container direction="column" alignContent="space-between" spacing={3}>
            <Grid item container direction="row" spacing={2} justifyContent="flex-start">
              <Grid item sm xs={6} md={1}>
                <Input
                  maxLength={5}
                  name="customer-abbreviation"
                  disabled={!!isCustomerSelected()}
                  label={translate("salutation")}
                  placeholder={translate("salutation")}
                  value={getCustomerValue("abbreviation")}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const abbreviationInputValue: string = e.currentTarget.value;

                    setCustomerInputValue("abbreviation", abbreviationInputValue ?? undefined);
                    setCustomerValue("abbreviation", abbreviationInputValue ?? undefined);
                  }}
                />
              </Grid>
              <Grid item sm xs={6} md={2}>
                <AutoSuggest
                  required
                  error={fieldHasError("name")}
                  errorMessage={getErrorByField("name")}
                  noOptionsText={translate("searchOrAddACustomer")}
                  label={translate("firstName")}
                  name="name"
                  inputValue={getCustomerValue("name")}
                  getOptionLabel={(customer?: Customer) => getOptionLabelCustomer(customer)}
                  getOptionSelected={(option: Customer, value: Customer) => option.id === value.id}
                  filterOptions={(options) => options}
                  placeholder={translate("firstName")}
                  onSearch={(value) =>
                    CustomerService.getAll({
                      companyId: currentUser?.companyId,
                      status: Status.ACTIVE,
                      include: JSON.stringify([
                        { association: "timezone" },
                        { association: "contacts" },
                      ]),
                      searchByOr: JSON.stringify([
                        { name: value },
                        { last_name: value },
                        { phone: value },
                        { mobile: value },
                        { email: value },
                        { destination_mobile: value },
                      ]),
                    })
                  }
                  onChange={(customer: Customer) => {
                    handleSetCustomerFields(customer);
                  }}
                  onChangeInput={(value) => {
                    setCustomerInputValue("name", value);
                  }}
                />
              </Grid>
              <Grid item sm xs={6} md={2}>
                <Input
                  name="lastName"
                  label={translate("lastName")}
                  placeholder={translate("lastName")}
                  value={getCustomerValue("lastName")}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const lasNameInputValue: string = e.currentTarget.value;

                    setCustomerInputValue("lastName", lasNameInputValue);
                    setCustomerValue("lastName", lasNameInputValue);
                  }}
                  required
                  error={fieldHasError("lastName")}
                  errorMessage={getErrorByField("lastName")}
                />
              </Grid>
            </Grid>

            <Grid item container direction="row" spacing={2} justifyContent="flex-start">
              <Grid item xs={12} md={3}>
                <InputPhone
                  disabled={!!isCustomerSelected()}
                  label={translate("mobile")}
                  {...phoneInputSettings(currentUser?.company, removeDefaultCountry)}
                  value={getCustomerValue(mobilePhoneInputName)}
                  onChange={(value) => {
                    setRemoveDefaultCountry(true);
                    if (phoneNumberIsValid(value)) {
                      if (!isDelivery && !isDeliveryInputOnFocus) {
                        const destinationMobile = getCustomerValue("destinationMobile");
                        const pickUpMobile = getCustomerValue("mobile");
                        const shouldUpdateDestinationMobile = pickUpMobile === destinationMobile;

                        setData(
                          (prev) =>
                            ({
                              ...(prev || {}),
                              mobile: value,
                              destinationMobile: shouldUpdateDestinationMobile
                                ? value
                                : prev?.destinationMobile,
                            } as CustomerInfoType)
                        );
                        update({
                          customer: {
                            ...data,
                            mobile: value,
                            destinationMobile: shouldUpdateDestinationMobile
                              ? value
                              : destinationMobile,
                          },
                          contacts: dataContacts,
                        });
                      } else {
                        setData(
                          (prev) =>
                            ({ ...(prev || {}), destinationMobile: value } as CustomerInfoType)
                        );
                        update({
                          customer: {
                            ...data,
                            destinationMobile: value,
                          },
                          contacts: dataContacts,
                        });
                      }
                    }
                  }}
                  placeholder="61430111900"
                  onFocus={() => {
                    if (isDelivery) setIsDeliveryInputOnFocus(true);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InputPhone
                  disabled={!!isCustomerSelected()}
                  label={translate("phoneNumber")}
                  {...phoneInputSettings(currentUser?.company, removeDefaultCountry)}
                  value={getCustomerValue("phone")}
                  onChange={(value) => {
                    setRemoveDefaultCountry(true);
                    if (phoneNumberIsValid(value)) {
                      setData(
                        (prev) =>
                          ({
                            ...(prev || {}),
                            phone: value,
                          } as CustomerInfoType)
                      );

                      if (!isDelivery && !isDeliveryInputOnFocus) {
                        update({
                          customer: {
                            ...data,
                            phone: value,
                          },
                          contacts: dataContacts,
                        });
                      } else {
                        update({
                          customer: {
                            ...data,
                            phone: value,
                          },
                          contacts: dataContacts,
                        });
                      }
                    }
                  }}
                  placeholder="61430111900"
                  onFocus={() => {
                    if (isDelivery) setIsDeliveryInputOnFocus(true);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Input
                  fullWidth
                  disabled={!!isCustomerSelected()}
                  name="email"
                  type="text"
                  label={translate("email")}
                  placeholder={`Customer email`}
                  value={getCustomerValue("email")}
                  onChange={(event) => {
                    const { value } = event.target;

                    setCustomerInputValue("email", value);
                    setCustomerValue("email", value);
                  }}
                  required
                  error={fieldHasError("emailCustomer")}
                  errorMessage={getErrorByField("emailCustomer")}
                />
              </Grid>
            </Grid>

            <Grid container item direction="row" spacing={2} justifyContent="flex-start">
              <Grid item xs={12} md={2}>
                <AutoSuggest
                  label={translate("language")}
                  name="customer-language"
                  options={languageOptions}
                  onChange={(language?: Languages) => {
                    setCustomerInputValue("languageId", language?.id ?? undefined);
                    setCustomerValue("languageId", language?.id ?? undefined);
                  }}
                  inputValue={
                    customerLanguage?.name ? customerLanguage?.name : englishLanguage?.name
                  }
                  getOptionLabel={(language: Languages) => language.name || ""}
                  getOptionSelected={(language: Languages, value: Languages) =>
                    language.id === value.id
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AutoSuggest
                  disabled={!!isCustomerSelected()}
                  label={translate("timezone")}
                  name="customer-timezone"
                  options={timezones}
                  onChange={(timezone?: Timezone) => {
                    setCustomerInputValue("timezoneId", timezone?.id ?? undefined);
                    setCustomerValue("timezoneId", timezone?.id ?? undefined);
                  }}
                  inputValue={timezoneValue}
                  onChangeInput={(value: string) => setTimezoneValue(value)}
                  getOptionLabel={(timezone: Timezone) =>
                    timezone && timezone?.gmtOffset && timezone?.nameFormatted
                      ? `GMT (${formatGMT(timezone?.gmtOffset)}) ${timezone?.nameFormatted
                          ?.replace("/", " / ")
                          .replace("_", "")}`
                      : ""
                  }
                  getOptionSelected={(option: Timezone, value: Timezone) =>
                    option?.id === value?.id
                  }
                  required
                  error={fieldHasError("timezoneId")}
                  errorMessage={getErrorByField("timezoneId")}
                />
              </Grid>
              <Grid container item direction="row" xs={12} md={1}>
                {isCustomerSelected() && (
                  <Grid item xs={12} md={4}>
                    {renderEditCustomerButton()}
                  </Grid>
                )}
                <Grid item xs={12} md={4}>
                  <FiPlusCircle
                    className={classes.iconAddButton}
                    color="#308CDB"
                    onClick={onAddSecondContact}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Stack>
      </>
    );
  };

  const renderContactsFields = () => {
    if (dataContacts && dataContacts.length > 0) {
      return dataContacts?.map((contact, index) => {
        const cantEdit = !!contact?.id;

        if (contact?.valid !== false) {
          return (
            <Stack spacing={1} direction="row" alignItems="center" key={contact?.id + "_customer"}>
              <Grid container direction="row" spacing={2} justifyContent="space-between">
                <Grid item sm md={4} lg={4}>
                  <AutoSuggest
                    disabled={cantEdit}
                    noOptionsText={translate("searchOrAddAContact")}
                    label={translate("secondContact")}
                    name="secondContact"
                    inputValue={contact?.firstName ?? ""}
                    getOptionLabel={(contact?: Contact) => `${contact?.firstName ?? ""}`}
                    getOptionSelected={(option: Contact, value: Contact) => option.id === value.id}
                    filterOptions={(options) => handleFilterContactOptions(options)}
                    placeholder={translate("searchByContactNameMobilePhoneOrEmailAddress")}
                    onSearch={(value) =>
                      ContactService.getAll({
                        companyId: currentUser?.companyId,
                        customerId: customer?.id,
                        searchByOr: JSON.stringify([
                          { first_name: value },
                          { last_name: value },
                          { mobile_phone: value },
                          { email: value },
                        ]),
                      })
                    }
                    onChange={(contact: Contact) => setContactOnSearch(contact, index)}
                    onChangeInput={(value) => {
                      setContactValue("firstName", value, index);
                    }}
                  />
                </Grid>
                <Grid item sm xs={6} md={4} lg={4}>
                  <InputPhone
                    disabled={cantEdit}
                    label={translate("mobile")}
                    {...phoneInputSettings(currentUser?.company, removeDefaultCountry)}
                    value={getContactValue(contact, "mobilePhone") || ""}
                    onChange={(value) => {
                      setRemoveDefaultCountry(true);
                      if (phoneNumberIsValid(value)) {
                        setContactValue("mobilePhone", value, index);
                      }
                    }}
                    placeholder="61430111900"
                  />
                </Grid>
                <Grid item sm xs={6} md={4} lg={4}>
                  <Input
                    disabled={cantEdit}
                    fullWidth
                    name="email"
                    type="text"
                    label={translate("email")}
                    placeholder={translate("contactEmail")}
                    value={contact?.email ?? ""}
                    onChange={(event) => {
                      const { value } = event.target;

                      setContactValue("email", value, index);
                    }}
                    required
                    error={fieldHasError("email")}
                    errorMessage={getErrorByField("email")}
                  />
                </Grid>
              </Grid>
              <FiXCircle
                className={classes.iconRemoveButton}
                color="#F97066"
                data-cy="button-delete-customer"
                onClick={() => onRemoveSecondContact(index)}
              />
            </Stack>
          );
        }

        return null;
      });
    }
  };

  const renderCustomerMaintenance = (isDelivery?: boolean) => {
    return (
      <CustomerMaintenance
        isDelivery={isDelivery}
        modalMode
        customer={data}
        open={toggleEditCustomerModal}
        onClose={(customer) => {
          setToggleEditCustomerModal(false);
          if (customer) updateWithFirstContact(customer);
        }}
      />
    );
  };

  return (
    <>
      <Stack className={classes.header} spacing={2}>
        {renderCustomerFields()}
        {renderContactsFields()}
        {typeAddress === "delivery" && (
          <Button
            className={classes.copyButton}
            customColor={Color.SECONDARY}
            onClick={() => onCopyInfo && onCopyInfo(typeAddress)}
            startIcon={<FiCopy color="#FFF" />}
          >
            {translate(`copyInfoFrom${typeAddressInverted()}`)}
          </Button>
        )}
      </Stack>
      {renderCustomerMaintenance(isDelivery)}
    </>
  );
};

export default CustomerInfoComponent;
